import {
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField
} from '@mui/material';
import { MagnifyingGlassIcon, SlidersIcon, SortAscendingIcon, SortDescendingIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { genericMemo } from '.';

interface Props {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  toggleFilters: () => void;
  isLoading?: boolean;
  sortOptions: {
    label: string;
    type: 'asc' | 'desc';
    value: string;
  }[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

const SortControlComponent = ({
  sortBy,
  sortOrder,
  onSortChange,
  sortOptions
}: Pick<Props, 'sortBy' | 'sortOrder' | 'onSortChange' | 'sortOptions'>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSortSelect = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    onSortChange(newSortBy, newSortOrder);
    handleClose();
  };

  // Get the appropriate icon based on current sort
  const getSortIcon = () => {
    if (!sortBy) return <SortDescendingIcon size={20} />;

    return sortOrder === 'asc' ? <SortAscendingIcon size={20} /> : <SortDescendingIcon size={20} />;
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        color="primary"
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          width: 48,
          height: 48,
          color: sortBy ? 'primary.main' : 'text.secondary',
          '&:hover': { backgroundColor: 'action.hover' }
        }}
      >
        {getSortIcon()}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'sort-button',
          }
        }}
      >
        {
          sortOptions.map((option, index) => (
            <MenuItem key={index} onClick={() => handleSortSelect(option.value, option.type)}>
              {option.label}
            </MenuItem>
          ))
        }
      </Menu>
    </>
  );
};

const SortControl = genericMemo(SortControlComponent);


const SearchBar = ({
  searchQuery,
  setSearchQuery,
  toggleFilters,
  isLoading,
  sortBy,
  sortOrder,
  onSortChange,
  sortOptions
}: Props) => {
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      // The search will be triggered automatically by the debounced value change
      event.preventDefault();
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems='center'>
      <TextField
        fullWidth
        placeholder="Search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={handleKeyPress}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlassIcon size={20} />
              </InputAdornment>
            ),
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : undefined
          }
        }}
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          }
        }}
      />
      <SortControl
        sortOptions={sortOptions}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={onSortChange}
      />
      <IconButton
        color="primary"
        onClick={toggleFilters}
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          width: 48,
          height: 48
        }}
      >
        <SlidersIcon size={24} />
      </IconButton>
    </Stack>
  );
};


export default genericMemo(SearchBar);